import {Component} from 'panel';

import {immutableSplice, replaceByIndex} from 'common/util';
import {qbStyleAttrs} from 'common/query-builder/query-builder/util';
import formulaSvg from 'common/assets/images/query/formula.svg';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import type {SelectDropdown} from 'common/components/select';
import type {MenuChangeEvent} from 'common/components/select/types';
import {ProdTrackingEvent} from 'common/report/insights/tracking';
import {InsightsQueryError} from 'common/report/insights/util/types';

import {Formula, FormulaTrackingInfo} from './types';
import {template} from './template';
import css from './index.inline.styl';

type InsightsFormulaState = {
  formulas: Formula[];
  isNewPaneOpen: boolean;
};

type InsightsFormulaAttrs = {
  'disabled-reason'?: string;
  'project-id': number;
};

type InsightsFormulaJsxProps = {
  attrs: InsightsFormulaAttrs;
  props: {
    formulas: Formula[];
  };
  on?: {
    update: (ev: CustomEvent<{formulas: Formula[]; trackingInfo: FormulaTrackingInfo}>) => void;
  };
};

export class InsightsFormula extends Component<InsightsFormulaState> {
  private stickyHeaderElm: HTMLElement = null;
  _error: Nullable<InsightsQueryError> = null;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'disabled-reason': {type: `string`, description: `message to display if formula should be disabled`},
      'project-id': `number`,
    };
  }

  connectedCallback() {
    super.connectedCallback();
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,

      defaultState: {
        formulas: [],
        isNewPaneOpen: false,
      },

      helpers: {
        buttonTheme: () => qbStyleAttrs().buttonTheme,

        handleMenuChange: (ev: MenuChangeEvent) => {
          this.update({isNewPaneOpen: ev.detail.isOpen});
        },

        handleUpdate: (ev, clauseIndex) => {
          const {formulas} = this.state;
          const {formula} = ev.detail;
          const updatedFormulas = replaceByIndex(formulas, clauseIndex, formula);

          this.dispatchFormulasUpdate(updatedFormulas, {
            event: ProdTrackingEvent.EditFormulaClause,
            formula,
          });
        },

        handleSaveNew: (ev) => {
          const {label, value} = ev.detail;

          const newFormula = {label, value};
          const updatedFormulas = [...this.state.formulas, newFormula];

          /**
           * @todo [pablo] this relies on TS for not breaking, what would be a more declarative API
           * for closing the select dropdown when something inside is clicked?
           */
          this.el.querySelector<SelectDropdown>(`[elref="addFormulaButton"]`).setDropMenuIsOpen(false);

          this.dispatchFormulasUpdate(updatedFormulas, {
            event: ProdTrackingEvent.AddFormulaClause,
            formula: newFormula,
          });
        },

        handleDelete: (clauseIndex) => {
          const removedFormula = this.state.formulas[clauseIndex];
          const updatedFormulas = immutableSplice(this.state.formulas, clauseIndex, 1);

          this.dispatchFormulasUpdate(updatedFormulas, {
            event: ProdTrackingEvent.RemoveFormulaClause,
            formula: removedFormula,
          });
        },

        getTooltipAttrs: () => {
          if (this.attr(`disabled-reason`)) {
            return {text: this.attr(`disabled-reason`), placement: Placement.Right};
          }

          return {
            text: `Use events and cohorts as variables in simple arithmetic calculations.`,
            size: DisplaySize.Small,
            'image-src': formulaSvg,
            placement: Placement.Right,
          };
        },

        onInsertStickyHeader: (el: HTMLElement) => {
          this.stickyHeaderElm = el;
        },

        getStickyHeaderElm: () => {
          return this.stickyHeaderElm;
        },
      },
    };
  }

  dispatchFormulasUpdate(updatedFormulas: Formula[], trackingInfo: FormulaTrackingInfo) {
    this.dispatchEvent(
      new CustomEvent(`update`, {
        detail: {formulas: updatedFormulas, trackingInfo},
      }),
    );
    this.update({formulas: updatedFormulas, isNewPaneOpen: false});
  }

  set formulas(formulas) {
    this.update({formulas});
  }

  set error(error: InsightsQueryError) {
    this._error = error;
    this.update();
  }

  get error() {
    return this._error;
  }
}

export const InsightsFormulaEl = defineAndMakeJsxFactory<InsightsFormulaJsxProps>(`insights-formula`, InsightsFormula);
export const InsightsFormulaReact = panel2react<InsightsFormulaJsxProps>(`insights-formula`, InsightsFormula);
